import { ProtocolConsumptionRealizeTypeEnum } from '../../models/Enums/ProtocolConsumptionRealizeTypeEnum';

/**
 * Modalidad de una visita en los consumos del médico y en el detalle de su
 * liquidación. Hay dos fuentes:
 *
 *  - `realizedType` (1 Remota, 2 Presencial, 3 Domicilio, 4 No realizada): cómo
 *    se realizó efectivamente; lo fija el proceso de liquidación.
 *  - `currentConsumptionType` (VisitTypeEnum de MedicalProtocols: 0 Presencial,
 *    1 Telefónica, 2 Remota): la modalidad vigente de la visita en el protocolo.
 *
 * Regla acordada: mientras el consumo no tiene liquidación asociada se muestra
 * la modalidad vigente; con liquidación, la realizada. En el detalle de una
 * liquidación todo está liquidado, así que va la realizada y, si no vino, la
 * vigente, y recién después la etiqueta libre vieja (`tag1`).
 *
 * No se usa el `VisitTypeEnum` del front porque sus valores (2 = domicilio,
 * 3 = otro) no coinciden con los que manda el servidor.
 */

export type ModalityVariant = 'onsite' | 'phone' | 'remote' | 'home' | 'none';
export type ModalitySource = 'realized' | 'current' | 'tag';

export interface Modality {
    label: string;
    variant: ModalityVariant;
    source: ModalitySource;
}

const REALIZED_TYPE_MODALITY: Record<number, Omit<Modality, 'source'>> = {
    [ProtocolConsumptionRealizeTypeEnum.RealizedRemotely]: { label: 'Remota', variant: 'remote' },
    [ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace]: { label: 'Presencial', variant: 'onsite' },
    [ProtocolConsumptionRealizeTypeEnum.RealizedAtHome]: { label: 'Domicilio', variant: 'home' },
    [ProtocolConsumptionRealizeTypeEnum.NotRealized]: { label: 'No realizada', variant: 'none' },
};

/** `currentConsumptionType`, VisitTypeEnum del servidor: 0 OnSite, 1 Telephone, 2 Remote. */
const CONSUMPTION_TYPE_MODALITY: Record<number, Omit<Modality, 'source'>> = {
    0: { label: 'Presencial', variant: 'onsite' },
    1: { label: 'Telefónica', variant: 'phone' },
    2: { label: 'Remota', variant: 'remote' },
};

export const realizedModality = (realizedType: number | null | undefined): Modality | undefined => {
    const found = realizedType !== undefined && realizedType !== null ? REALIZED_TYPE_MODALITY[realizedType] : undefined;
    return found ? { ...found, source: 'realized' } : undefined;
};

export const currentModality = (currentConsumptionType: number | null | undefined): Modality | undefined => {
    const found = currentConsumptionType !== undefined && currentConsumptionType !== null ? CONSUMPTION_TYPE_MODALITY[currentConsumptionType] : undefined;
    return found ? { ...found, source: 'current' } : undefined;
};

/** Un consumo tiene liquidación asociada cuando `settlementId` trae un id. */
export const hasSettlement = (item: { settlementId?: number | null }): boolean =>
    typeof item.settlementId === 'number' && item.settlementId > 0;

/**
 * Modalidad para la lista de consumos del médico: con liquidación, la
 * realizada (`realizedType`); sin liquidación, la vigente (`currentConsumptionType`).
 * Sin dato reconocible, `undefined` (la grilla muestra un guion).
 */
export const practitionerConsumptionModality = (
    item: { settlementId?: number | null; realizedType?: number | null; currentConsumptionType?: number | null }
): Modality | undefined =>
    hasSettlement(item) ? realizedModality(item.realizedType) : currentModality(item.currentConsumptionType);

/**
 * Modalidad para el detalle de una liquidación a médico:
 * `realizedType` → `currentConsumptionType` → etiqueta libre `tag1` → nada.
 */
export const settlementDetailModality = (
    detail: { realizedType?: number | null; currentConsumptionType?: number | null; tag1?: string | null }
): Modality | undefined => {
    const known = realizedModality(detail.realizedType) ?? currentModality(detail.currentConsumptionType);
    if (known) return known;
    const tag = (detail.tag1 || '').trim();
    return tag ? { label: tag, variant: 'none', source: 'tag' } : undefined;
};

export const modalityLabel = (modality: Modality | undefined): string => modality?.label ?? '—';
